import api from './client';
import type {
  AnalyticsData,
  ApprovalComment,
  ApprovalRequest,
  BedAcceptedRow,
  BedCalculateResponse,
  BedCommitResponse,
  BedPreviewResponse,
  BodySite,
  Constraint,
  ConstraintSet,
  ConstraintSetList,
  ConstraintSetVersion,
  ConstraintSuggestion,
  FinalizeResponse,
  GenerateSuggestionsResponse,
  ImportCandidate,
  ImportedDoseRow,
  MapStructuresPayload,
  OrganAtRisk,
  PaginatedResponse,
  ReirradiationAuditEvent,
  ReirradiationCase,
  ReviewsDueResponse,
  TreatmentCourse,
  User,
  VersionDiff,
} from '../types';
import type { OneOffSet } from '../types/oneoffs';

// Auth
export const login = async (username: string, password: string) => {
  // Fetch CSRF cookie before login POST
  await api.get('/api/accounts/csrf/');
  return api.post<User>('/api/accounts/login/', { username, password });
};

export const logout = () => api.post('/api/accounts/logout/');

export const getCurrentUser = () => api.get<User>('/api/accounts/me/');

// User Management
export const getUsers = () =>
  api.get<PaginatedResponse<User>>('/api/accounts/users/', { params: { page_size: 200 } });

export const createUser = (data: {
  username: string;
  password: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  title?: string;
  department?: string;
  is_approver: boolean;
  is_admin: boolean;
  approves_body_sites?: number[];
}) => api.post<User>('/api/accounts/users/', data);

export const updateUser = (id: number, data: Partial<User>) =>
  api.patch<User>(`/api/accounts/users/${id}/`, data);

export const toggleUserActive = (id: number) =>
  api.post<User>(`/api/accounts/users/${id}/toggle_active/`);

export const resetUserPassword = (id: number, password: string) =>
  api.post(`/api/accounts/users/${id}/reset_password/`, { password });

// Dashboard aggregator — single call replaces getBodySites + getConstraintSets
// + getReviewsDue('all') on DashboardPage mount. Cuts ~300-400ms of HTTP
// overhead on cold loads (3 round-trips → 1). DashboardPage falls back to the
// individual endpoints if this 404s (older deploy / kill-switch).
export interface DashboardBundle {
  body_sites: BodySite[];
  constraint_sets: ConstraintSetList[];
  reviews_due: ReviewsDueResponse;
  // v0.6.0.17: surface OneOffSets per body site on the dashboard.
  // Optional in the type so DashboardPage tolerates an older deploy
  // (pre-v0.6.0.17 backend) where the field is absent — the legacy
  // fallback path also lives without it.
  one_off_sets?: OneOffSet[];
}
export const getDashboard = () =>
  api.get<DashboardBundle>('/api/dashboard/');

// Body Sites
export const getBodySites = () =>
  api.get<PaginatedResponse<BodySite>>('/api/body-sites/');

export const exportBodySite = (id: number) =>
  api.get(`/api/body-sites/${id}/export/`, { responseType: 'blob' });

// OARs — server returns a bare array (pagination_class=None on the viewset).
// The reference dataset is small (~200 rows) and every caller needs the full
// list (dropdowns/autocomplete), so a single round-trip beats paginating.
export const getOARs = (params?: { search?: string; anatomical_group?: string }) =>
  api.get<OrganAtRisk[]>('/api/oars/', { params });

export const createOAR = (data: { name: string; anatomical_group?: string }) =>
  api.post<OrganAtRisk>('/api/oars/', data);

// Constraint Sets
export const getConstraintSets = (params?: {
  body_site?: number;
  is_active?: boolean;
  search?: string;
}) =>
  api.get<PaginatedResponse<ConstraintSetList>>('/api/constraint-sets/', {
    params: { ...params, page_size: 100 },
  });

export const getConstraintSet = (id: number) =>
  api.get<ConstraintSet>(`/api/constraint-sets/${id}/`);

export const createConstraintSet = (data: {
  body_site: number;
  name: string;
  location: string;
  fractions: number;
  description?: string;
}) => api.post<ConstraintSet>('/api/constraint-sets/', data);

export const deprecateConstraintSet = (id: number) =>
  api.post<ConstraintSet>(`/api/constraint-sets/${id}/deprecate/`);

export const reaffirmConstraintSet = (id: number) =>
  api.post<ConstraintSet>(`/api/constraint-sets/${id}/reaffirm/`);

/** `mine: true` scopes to the caller's own body sites (requires can_approve)
 *  and drops sets an editor already assigned to them explicitly. That derived
 *  list replaced the nightly auto-assignment cron — "overdue" is a function of
 *  last_reviewed_at, so it's computed on read rather than written into rows. */
export const getReviewsDue = (
  status: 'overdue' | 'due_soon' | 'all' = 'all',
  opts: { mine?: boolean } = {},
) =>
  api.get<ReviewsDueResponse>('/api/constraint-sets/reviews_due/', {
    params: { status, ...(opts.mine ? { mine: 'true' } : {}) },
  });

// Annual review assignments (v0.6.0.12). The bulk-assign endpoint creates
// AnnualReviewAssignment rows for each (set, reviewer) pair; the reviewer's
// reaffirm call auto-completes the assignment server-side.
export const bulkAssignAnnualReview = (
  payload: import('../types').BulkAssignReviewRequest,
) =>
  api.post<import('../types').BulkAssignReviewResponse>(
    '/api/constraint-sets/bulk_assign_review/',
    payload,
  );

// Returns assignments scoped to the calling user by default (?assigned_to=me).
// Backend silently scopes non-admin callers to themselves regardless of the
// assigned_to param.
export const listMyAnnualReviewAssignments = (params?: { pending?: boolean }) =>
  api.get<{ count?: number; results?: import('../types').AnnualReviewAssignment[] }
    | import('../types').AnnualReviewAssignment[]>(
    '/api/annual-review-assignments/',
    {
      params: {
        assigned_to: 'me',
        pending: params?.pending ? 'true' : undefined,
      },
    },
  );

export const exportBulkXlsx = (constraintSetIds: number[]) =>
  api.post('/api/constraint-sets/export_bulk_xlsx/', { constraint_set_ids: constraintSetIds }, {
    responseType: 'blob',
  });

export const getConsistencyCheck = () =>
  api.get<{ inconsistency_count: number; inconsistencies: any[] }>(
    '/api/constraint-sets/consistency_check/'
  );

// Versions
export const getVersion = (id: number) =>
  api.get<ConstraintSetVersion>(`/api/versions/${id}/`);

export const getVersionsBySet = (constraintSetId: number) =>
  api.get<PaginatedResponse<ConstraintSetVersion>>('/api/versions/', {
    params: { constraint_set: constraintSetId, page_size: 50 },
  });

export const createVersion = (data: {
  constraint_set: number;
  change_summary: string;
}) => api.post<ConstraintSetVersion>('/api/versions/', data);

export const submitVersion = (id: number, requiredReviewers?: number[]) =>
  api.post<ConstraintSetVersion>(`/api/versions/${id}/submit/`, {
    required_reviewers: requiredReviewers ?? [],
  });

export const getVersionDiff = (id: number) =>
  api.get<VersionDiff>(`/api/versions/${id}/diff/`);

export const cloneVersion = (id: number) =>
  api.post<ConstraintSetVersion>(`/api/versions/${id}/clone/`);

export const updateConstraints = (
  versionId: number,
  constraints: Partial<Constraint>[],
  changeSummary: string,
  sourceOneOffIds?: number[]
) =>
  api.post<ConstraintSetVersion>(`/api/versions/${versionId}/constraints/`, {
    constraints,
    change_summary: changeSummary,
    // Tagged-reuse audit hook (E3'). Server validates each id has a field
    // match against the constraints payload; spoofed ids return 400.
    ...(sourceOneOffIds && sourceOneOffIds.length ? { source_one_off_ids: sourceOneOffIds } : {}),
  });

// Single-constraint detail fetch. Used by "Save as one-off" to read the FULL
// row including endpoint_category and notes (Codex finding #5: the editor's
// local state strips both).
export const getConstraint = (id: number) =>
  api.get<Constraint>(`/api/constraints/${id}/`);

export const unapproveVersion = (id: number) =>
  api.post<ConstraintSetVersion>(`/api/versions/${id}/unapprove/`);

export const deleteDraftVersion = (id: number) =>
  api.delete(`/api/versions/${id}/delete_draft/`);

export const getVersionsByFractions = (fractions: number, excludeSetId?: number) =>
  api.get('/api/versions/by_fractions/', {
    params: { fractions, exclude_set: excludeSetId },
  });

export const exportVersionXlsx = (id: number) =>
  api.get(`/api/versions/${id}/export_xlsx/`, { responseType: 'blob' });

export const downloadRayStationScript = (id: number) =>
  api.get(`/api/versions/${id}/raystation_script/`, { responseType: 'blob' });

export const downloadVerificationScript = (id: number) =>
  api.get(`/api/versions/${id}/verification_script/`, { responseType: 'blob' });

// RayStation Management
export const downloadAuditScript = () =>
  api.get('/api/raystation/management/audit_script/', { responseType: 'blob' });

export const downloadUploadScript = (versionId: number) =>
  api.get('/api/raystation/management/upload_script/', {
    params: { version_id: versionId },
    responseType: 'blob',
  });

export const getApprovedSets = () =>
  api.get('/api/raystation/management/approved_sets/');

export const compareTemplates = (templateData: any) =>
  api.post('/api/raystation/management/compare/', templateData);

// Approvals
export const getPendingApprovals = () =>
  api.get<ApprovalRequest[]>('/api/approvals/requests/pending/');

export const getApprovalRequest = (id: number) =>
  api.get<ApprovalRequest>(`/api/approvals/requests/${id}/`);

export const submitReview = (data: {
  approval_request: number;
  decision: 'approved';
  comment: string;
}) => api.post('/api/approvals/reviews/', data);

export const addApprovalComment = (data: {
  approval_request: number;
  text: string;
}) => api.post('/api/approvals/comments/', data);

export const adminOverride = (approvalRequestId: number, reason: string) =>
  api.post(`/api/approvals/requests/${approvalRequestId}/override/`, { reason });

// Reviewer assignment (2026-04-19 feature additions; v0.6.0.14 extended
// to optionally accept a list of body site IDs for union filtering).
export const getApprovers = (bodySiteId?: number | number[]) => {
  let params: { body_site?: string } | undefined;
  if (Array.isArray(bodySiteId)) {
    if (bodySiteId.length > 0) {
      // Backend accepts a comma-separated list on the body_site param.
      params = { body_site: bodySiteId.join(",") };
    } // empty list = no filter (same as omitting)
  } else if (bodySiteId !== undefined) {
    params = { body_site: String(bodySiteId) };
  }
  return api.get<User[]>('/api/accounts/users/approvers/', { params });
};

export const updateRequiredReviewers = (
  approvalRequestId: number,
  add: number[],
  remove: number[],
) =>
  api.post<ApprovalRequest>(
    `/api/approvals/requests/${approvalRequestId}/update-reviewers/`,
    { add, remove },
  );

// Constraint-level approvals
export const approveConstraint = (data: {
  approval_request: number;
  constraint: number;
}) => api.post('/api/approvals/constraint-approvals/', data);

export const approveAllConstraints = (approvalRequestId: number) =>
  api.post('/api/approvals/constraint-approvals/approve_all/', {
    approval_request: approvalRequestId,
  });

// Change request comments
export const addChangeRequest = (data: {
  approval_request: number;
  text: string;
  constraint: number;
  is_change_request: boolean;
}) => api.post<ApprovalComment>('/api/approvals/comments/', data);

export const resolveComment = (commentId: number) =>
  api.post<ApprovalComment>(`/api/approvals/comments/${commentId}/resolve/`);

export const unresolveComment = (commentId: number) =>
  api.post<ApprovalComment>(`/api/approvals/comments/${commentId}/unresolve/`);

// Import candidates
export const getImportCandidates = (
  params: {
    /** A single fractionation, or a comma-separated list ("5,25,30") for the
     *  cross-fractionation one-off set builder — every returned candidate
     *  carries its own `fractions`. */
    fractions?: number | string;
    body_site?: number;
    exclude_set?: number;
    include_versions?: string;
  },
  config?: { signal?: AbortSignal },
) =>
  api.get<ImportCandidate[]>('/api/versions/import-candidates/', {
    params,
    ...config,
  });

// Analytics
export const getAnalytics = () =>
  api.get<AnalyticsData>('/api/versions/analytics/');

// BED converter. v0.6.0.15: optional defaultAlphaBeta overrides the
// per-OAR seeded value for every row, used as the modal-level default
// for fraction-change conversions. Omit to use OAR-seeded values.
export const bedPreview = (
  sourceVersionId: number,
  targetFractions: number,
  defaultAlphaBeta?: number,
) => {
  const body: { target_fractions: number; default_alpha_beta?: number } = {
    target_fractions: targetFractions,
  };
  if (defaultAlphaBeta !== undefined) body.default_alpha_beta = defaultAlphaBeta;
  return api.post<BedPreviewResponse>(
    `/api/versions/${sourceVersionId}/bed_preview/`,
    body,
  );
};

export const bedCommit = (data: {
  source_version_id: number;
  target_fractions: number;
  source_version_lock: number;
  oar_fingerprint: string;
  accepted_rows: BedAcceptedRow[];
  /** v0.6.0.15: modal-level default α/β. MUST match the value used in
   *  the preview that produced these accepted_rows — otherwise the
   *  server re-derives with OAR-seeded α/β and the user's chosen
   *  default silently drops (clinical safety bug class). */
  default_alpha_beta?: number;
}) => api.post<BedCommitResponse>('/api/constraint-sets/bed_commit/', data);

export const bedCalculate = (data: {
  dose: number;
  source_fractions: number;
  target_fractions: number;
  alpha_beta: number;
}) => api.post<BedCalculateResponse>('/api/bed/calculate/', data);

// Reirradiation case workbench (v0.4)
//
// Phase 0 (parser) is deferred — listImportDose / uploadImportDose are not exported
// here yet. They land alongside the real CSV/XLSX fixtures.

export const listReirradiationCases = () =>
  api.get<PaginatedResponse<ReirradiationCase> | ReirradiationCase[]>('/api/reirradiation/cases/');

export const getReirradiationCase = (id: number) =>
  api.get<ReirradiationCase>(`/api/reirradiation/cases/${id}/`);

export const createReirradiationCase = (data: {
  name: string;
  body_site: number;
  base_constraint_set_version: number;
  target_fractions: number;
}) => api.post<ReirradiationCase>('/api/reirradiation/cases/', data);

export const updateReirradiationCase = (id: number, data: Partial<ReirradiationCase>) =>
  api.patch<ReirradiationCase>(`/api/reirradiation/cases/${id}/`, data);

export const deleteReirradiationCase = (id: number) =>
  api.delete<void>(`/api/reirradiation/cases/${id}/`);

export interface ReirradiationCoursePayload {
  course_label: string;
  total_dose_gy: number;
  fractions: number;
  treatment_end_date: string | null;
  dose_rows: Array<{
    imported_structure_name: string;
    mapped_oar_id?: number;
    total_dose_gy?: number;
    fractions?: number;
  }>;
}

export const addReirradiationCourse = (caseId: number, payload: ReirradiationCoursePayload) =>
  api.post<{ course: TreatmentCourse; dose_rows: ImportedDoseRow[] }>(
    `/api/reirradiation/cases/${caseId}/courses/`, payload,
  );

export const updateReirradiationCourse = (
  caseId: number,
  courseId: number,
  payload: ReirradiationCoursePayload,
) => api.patch<{ course: TreatmentCourse; dose_rows: ImportedDoseRow[] }>(
  `/api/reirradiation/cases/${caseId}/courses/${courseId}/`, payload,
);

export const mapReirradiationStructures = (caseId: number, payload: MapStructuresPayload) =>
  api.post<ImportedDoseRow[]>(
    `/api/reirradiation/cases/${caseId}/map_structures/`, payload,
  );

export const generateReirradiationSuggestions = (caseId: number) =>
  api.post<GenerateSuggestionsResponse>(
    `/api/reirradiation/cases/${caseId}/generate_suggestions/`,
  );

export const finalizeReirradiationCase = (caseId: number) =>
  api.post<FinalizeResponse>(`/api/reirradiation/cases/${caseId}/finalize/`);

export const getReirradiationAudit = (caseId: number) =>
  api.get<ReirradiationAuditEvent[]>(`/api/reirradiation/cases/${caseId}/audit/`);

export const patchReirradiationSuggestion = (
  suggestionId: number,
  data: {
    final_value_gy?: number;
    override_rationale?: string;
    cumulative_prior_bed_gy_override?: number | null;
  },
) => api.patch<ConstraintSuggestion>(`/api/reirradiation/suggestions/${suggestionId}/`, data);

// v0.6.1: per-course tissue-recovery factor override (0–1, or null to clear).
export const setReirradiationRecoveryFactor = (
  caseId: number,
  doseRowId: number,
  recoveryFactorOverride: number | null,
) =>
  api.post<{ dose_row_id: number; recovery_factor_override: number | null; policy_recovery_factor: number }>(
    `/api/reirradiation/cases/${caseId}/set_recovery_factor/`,
    { dose_row_id: doseRowId, recovery_factor_override: recoveryFactorOverride },
  );

// v0.6.1: "show the math" worksheet — per-row derivation + per-course breakdown.
export const getReirradiationWorksheet = (caseId: number) =>
  api.get<import('../types').ReirradiationWorksheet>(
    `/api/reirradiation/cases/${caseId}/worksheet/`,
  );
